/**
 * The language-ordering probe (spec 5 — an ordering task needs no distractors).
 *
 * Touch first. Pointer Events with `touch-action: none` means one implementation
 * covers finger, stylus and mouse, and the up/down buttons are a full keyboard
 * and screen-reader path for anyone who cannot drag at all. Every control is at
 * least 44px.
 *
 * The submitted permutation is `order[k] = index of the option the player put at
 * position k`, which is exactly what `encodeOrdering` in @geogame/rules ranks.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RoundPayloadOption } from '@geogame/shared';

const ROW_HEIGHT = 60;
const ROW_GAP = 8;
const STRIDE = ROW_HEIGHT + ROW_GAP;

export interface OrderingProbeProps {
  readonly options: readonly RoundPayloadOption[];
  readonly disabled: boolean;
  readonly onSubmit: (order: readonly number[]) => void;
}

export function OrderingProbe({
  options,
  disabled,
  onSubmit,
}: OrderingProbeProps): React.JSX.Element {
  const [order, setOrder] = useState<number[]>(() => options.map((_, i) => i));
  const [dragging, setDragging] = useState<{ position: number; offsetY: number } | null>(null);
  const dragRef = useRef<{ position: number; startY: number } | null>(null);

  useEffect(() => {
    setOrder(options.map((_, i) => i));
  }, [options]);

  const move = useCallback((from: number, to: number) => {
    setOrder((prev) => {
      if (to < 0 || to >= prev.length || from === to) return prev;
      const next = [...prev];
      const [taken] = next.splice(from, 1);
      if (taken === undefined) return prev;
      next.splice(to, 0, taken);
      return next;
    });
  }, []);

  const onPointerDown = (position: number) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { position, startY: e.clientY };
    setDragging({ position, offsetY: 0 });
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dy = e.clientY - drag.startY;
    const target = Math.max(
      0,
      Math.min(order.length - 1, drag.position + Math.round(dy / STRIDE)),
    );
    if (target !== drag.position) {
      move(drag.position, target);
      drag.startY += (target - drag.position) * STRIDE;
      drag.position = target;
      setDragging({ position: target, offsetY: e.clientY - drag.startY });
      return;
    }
    setDragging({ position: drag.position, offsetY: dy });
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
    setDragging(null);
  };

  return (
    <div className="flex flex-col gap-2">
      <ol className="flex list-none flex-col gap-2" style={{ margin: 0, padding: 0 }}>
        {order.map((optionIdx, position) => {
          const option = options[optionIdx];
          if (!option) return null;
          const isDragging = dragging?.position === position;
          return (
            <li key={option.id} style={{ height: ROW_HEIGHT }}>
              <div
                className="gg-order-row"
                data-dragging={isDragging ? 'true' : 'false'}
                style={{
                  height: ROW_HEIGHT,
                  touchAction: 'none',
                  transform: isDragging ? `translateY(${dragging.offsetY}px)` : undefined,
                }}
                onPointerDown={onPointerDown(position)}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              >
                <span className="gg-order-rank tabular-nums">{position + 1}</span>
                <span className="gg-order-label">{option.label}</span>
                <span className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    className="gg-order-nudge"
                    aria-label={`Move ${option.label} up`}
                    disabled={disabled || position === 0}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => move(position, position - 1)}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    className="gg-order-nudge"
                    aria-label={`Move ${option.label} down`}
                    disabled={disabled || position === order.length - 1}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => move(position, position + 1)}
                  >
                    ▼
                  </button>
                </span>
              </div>
            </li>
          );
        })}
      </ol>

      <button
        type="button"
        className="gg-btn gg-btn-primary"
        disabled={disabled}
        onClick={() => onSubmit(order)}
      >
        Lock in order
      </button>
    </div>
  );
}
